import { Injectable } from '@angular/core';
import { EupRoutesService } from '@core/eupRoutes.service';
import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { GlobalSettings } from '@core/globalSettings.service';
import { EupHttpHandler } from '@core/eupHttpHandler.service';
import { Consts } from '@shared/consts';
import { ShellContextService } from '../shell-context/shell-context.service';
import { CommunicationService } from '../client-communication/communication.service';
import { HttpHeaders } from '@angular/common/http';
import { AuthService, ImpersonateUserCredentials, SessionInfo } from './auth.service';
import { CookieReadWriteService } from './cookie-read-write.service';

@Injectable({
	providedIn: 'root',
})
export class UserDataService {
	constructor(
		private readonly http: EupHttpHandler,
		private readonly eupRoutesService: EupRoutesService,
		private readonly shellContextService: ShellContextService,
		private readonly communicationService: CommunicationService,
		private readonly cookiesService: CookieReadWriteService,
		private readonly authService: AuthService
	) {}

	proceedLoginFlow(sessionInfo: SessionInfo, credentials: { username: string }): Observable<GlobalSettings> {
		const isImpersonated = this.cookiesService.businessCookie.cookie?.impersonatedContactId ?? false;
		const getUserData$ = isImpersonated ? this.getImpersonatedUserData(sessionInfo) : this.getUserData(credentials.username);

		return getUserData$.pipe(
			catchError(error => {
				if (sessionInfo.sessionId.trim() !== '') {
					return this.invalidateSession(sessionInfo.sessionId).pipe(
						switchMap(_ => {
							return throwError(error);
						})
					);
				}

				return throwError(error);
			}),
			tap(userData => {
				this.communicationService.setSessionInfo(sessionInfo.sessionId);
				if (userData.companies && userData.companies[0]?.region) {
					this.communicationService.setRegion(userData.companies[0].region);
				}

				this.shellContextService.updateContext(ctx => {
					ctx.session.sessionId = sessionInfo.sessionId;
					ctx.security.accessToken = sessionInfo.accessToken;
					ctx.user.loggedInUserId = userData.contactId;
					ctx.user.roleType = userData.roleType;
					ctx.session.sessionType = Consts.SessionType;
				});

				if (userData.dateFormat || userData.selectedLanguage?.code) {
					this.shellContextService.updateContext(ctx => {
						ctx.UI.dateFormat = userData.dateFormat;
						ctx.UI.language = userData.selectedLanguage?.code || 'en-US';
					});
				}
			})
		);
	}

	invalidateSession(sessionId: string): Observable<boolean> {
		const headers = new HttpHeaders({
			'Content-Type': 'application/json',
		});

		return this.http.deleteWithoutCancel(this.invalidateSessionUrl(sessionId), { headers, observe: 'response' }).pipe(
			map((res: any) => {
				return res.body;
			}),
			catchError(() => {
				return of(false);
			})
		);
	}

	private invalidateSessionUrl = (sessionId: string) =>
		`${this.eupRoutesService.iTeroWebAuthApiUrl}session/invalidate?sessionId=${sessionId}&appId=mifl`;

	private getImpersonatedUserData(sessionInfo: SessionInfo): Observable<GlobalSettings> {
		const { companyId, impersonatedContactId } = this.cookiesService.businessCookie.cookie;

		return this.authService.impersonate(new ImpersonateUserCredentials(impersonatedContactId, companyId, sessionInfo?.sessionId));
	}

	private getUserData = (userName: string) =>
		this.http.get(this.eupRoutesService.login.getUserData(userName), { observe: 'response' }, true, true).pipe(
			map((res: any) => {
				return res.body;
			})
		);
}
